import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Sparkles,
  Target,
  BookOpen,
  CreditCard,
  Download,
  Upload,
  Brain,
  Rocket,
  Star,
  Zap,
  Users,
  Award,
  Clock,
  BarChart,
  Play,
  ArrowRight,
  Shield,
  Globe,
  TrendingUp,
  Lightbulb,
  Code,
  FileText,
  Map,
  Calendar,
  Bookmark,
  Gift,
  Crown
} from 'lucide-react';

const OnboardingTutorial = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isAnimating, setIsAnimating] = useState(false);

  const steps = [
    {
      id: 0,
      title: "Welcome to Rastor AI",
      subtitle: "Your AI-Powered Career Companion",
      description: "Discover personalized career paths and create step-by-step learning roadmaps with advanced AI technology.",
      icon: Sparkles,
      color: "from-emerald-500 to-teal-500",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl mb-6">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Transform Your Career with AI</h3>
            <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mx-auto">
              Join thousands of professionals who have discovered their perfect career path with our advanced AI algorithms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center mr-4">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white">AI-Powered Analysis</h4>
              </div>
              <p className="text-slate-300 text-sm">Advanced algorithms analyze your skills, experience, and goals to suggest the best career paths.</p>
            </div>

            <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-500/20 rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center mr-4">
                  <Map className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white">Personalized Roadmaps</h4>
              </div>
              <p className="text-slate-300 text-sm">Get detailed week-by-week learning plans tailored to your timeline and preferences.</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-6">
            <div className="flex items-center mb-3">
              <Crown className="w-5 h-5 text-purple-400 mr-2" />
              <h4 className="font-semibold text-purple-400">What You'll Get</h4>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2" />
                <span>Resume Analysis & ATS Score</span>
              </div>
              <div className="flex items-center text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2" />
                <span>3 Career Path Suggestions</span>
              </div>
              <div className="flex items-center text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2" />
                <span>Skill Gap Analysis</span>
              </div>
              <div className="flex items-center text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2" />
                <span>Progress Tracking</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 1,
      title: "Create Your Profile",
      subtitle: "Let AI Understand Your Background",
      description: "Upload your resume or manually create your profile to help our AI understand your skills and career goals.",
      icon: Upload,
      color: "from-blue-500 to-indigo-500",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center mr-4">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white">Upload Resume</h4>
                  <p className="text-slate-400 text-sm">PDF, DOC, DOCX</p>
                </div>
              </div>
              <div className="space-y-3 text-sm text-slate-300">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
                  <span>Automatic skill extraction</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
                  <span>Experience analysis</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
                  <span>ATS optimization</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-indigo-500 rounded-2xl flex items-center justify-center mr-4">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white">Manual Profile</h4>
                  <p className="text-slate-400 text-sm">Step-by-step form</p>
                </div>
              </div>
              <div className="space-y-3 text-sm text-slate-300">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-indigo-400 mr-2" />
                  <span>Detailed skill assessment</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-indigo-400 mr-2" />
                  <span>Career goal setting</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-indigo-400 mr-2" />
                  <span>Experience timeline</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-6">
            <div className="flex items-center mb-3">
              <Lightbulb className="w-5 h-5 text-amber-400 mr-2" />
              <h4 className="font-semibold text-amber-400">Pro Tip</h4>
            </div>
            <p className="text-slate-300 text-sm">
              The more detailed your profile, the better our AI can match you with relevant career paths and provide personalized recommendations.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: "AI Career Insights",
      subtitle: "Discover Your Perfect Path",
      description: "Our advanced AI analyzes your profile and suggests personalized career paths with detailed market insights.",
      icon: Brain,
      color: "from-purple-500 to-pink-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">What You'll Receive</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <span className="text-sm">3 Personalized Career Paths</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <span className="text-sm">Match Percentage Analysis</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <span className="text-sm">Detailed Reasoning & Insights</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">4</span>
                  </div>
                  <span className="text-sm">Salary Ranges & Growth</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">5</span>
                  </div>
                  <span className="text-sm">Required Skills & Gaps</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">6</span>
                  </div>
                  <span className="text-sm">Market Demand Analysis</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400 mb-1">95%</div>
              <div className="text-slate-300 text-sm">Accuracy Rate</div>
            </div>
            <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-500/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-teal-400 mb-1">150+</div>
              <div className="text-slate-300 text-sm">Career Paths</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-cyan-400 mb-1">25K+</div>
              <div className="text-slate-300 text-sm">Users Transformed</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-6">
            <div className="flex items-center mb-3">
              <Gift className="w-5 h-5 text-green-400 mr-2" />
              <h4 className="font-semibold text-green-400">Free Preview Available</h4>
            </div>
            <p className="text-slate-300 text-sm">
              See one career path analysis for free. Upgrade to Starter plan for all 3 paths and detailed roadmaps!
            </p>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "Personalize Your Roadmap",
      subtitle: "Tailored to Your Goals",
      description: "Answer a few quick questions to get a highly personalized learning roadmap that fits your timeline and preferences.",
      icon: Target,
      color: "from-orange-500 to-red-500",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Timeline Questions</h4>
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <Clock className="w-4 h-4 text-orange-400 mr-3" />
                  <span className="text-sm">When do you want to transition?</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <Calendar className="w-4 h-4 text-orange-400 mr-3" />
                  <span className="text-sm">How many hours per week?</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <BookOpen className="w-4 h-4 text-orange-400 mr-3" />
                  <span className="text-sm">What's your learning style?</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <CreditCard className="w-4 h-4 text-orange-400 mr-3" />
                  <span className="text-sm">What's your budget?</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/20 rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Personalization Benefits</h4>
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-4 h-4 text-red-400 mr-3" />
                  <span className="text-sm">Custom learning schedule</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-4 h-4 text-red-400 mr-3" />
                  <span className="text-sm">Curated resources & courses</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-4 h-4 text-red-400 mr-3" />
                  <span className="text-sm">Progress tracking system</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-4 h-4 text-red-400 mr-3" />
                  <span className="text-sm">PDF roadmap download</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-2xl p-6">
            <div className="flex items-center mb-3">
              <Zap className="w-5 h-5 text-blue-400 mr-2" />
              <h4 className="font-semibold text-blue-400">Smart Recommendations</h4>
            </div>
            <p className="text-slate-300 text-sm">
              Our AI considers your current skills, target role requirements, and market trends to create the most effective learning path for your success.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "Choose Your Plan",
      subtitle: "Flexible Subscription Options",
      description: "Start free and upgrade to unlock premium features. Cancel anytime, no hidden fees.",
      icon: Crown,
      color: "from-yellow-500 to-orange-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-2xl p-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl mb-4">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Premium Features Included</h4>
              <p className="text-slate-300 text-sm">Everything you need for career transformation</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400 mr-3" />
                  <div>
                    <div className="font-semibold text-white">All 3 Career Paths</div>
                    <div className="text-xs text-slate-400">Complete analysis & insights</div>
                  </div>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400 mr-3" />
                  <div>
                    <div className="font-semibold text-white">Detailed Roadmaps</div>
                    <div className="text-xs text-slate-400">Week-by-week learning plans</div>
                  </div>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400 mr-3" />
                  <div>
                    <div className="font-semibold text-white">Progress Tracking</div>
                    <div className="text-xs text-slate-400">Monitor your achievements</div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-400 mr-3" />
                  <div>
                    <div className="font-semibold text-white">PDF Export</div>
                    <div className="text-xs text-slate-400">Beautiful roadmap downloads</div>
                  </div>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-400 mr-3" />
                  <div>
                    <div className="font-semibold text-white">Resource Library</div>
                    <div className="text-xs text-slate-400">Curated learning materials</div>
                  </div>
                </div>
                <div className="flex items-center text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-400 mr-3" />
                  <div>
                    <div className="font-semibold text-white">Lifetime Access</div>
                    <div className="text-xs text-slate-400">No recurring charges</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-green-400 mb-1">Free</div>
              <div className="text-slate-300 text-sm">Start Exploring</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">₹499</div>
              <div className="text-slate-300 text-sm">1 Month Access</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-purple-400 mb-1">₹2,999</div>
              <div className="text-slate-300 text-sm">Full Year (Best Value)</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: "Start Your Journey",
      subtitle: "Ready to Transform Your Career",
      description: "Begin your learning journey with your personalized roadmap. Track progress, complete tasks, and achieve your career goals.",
      icon: Rocket,
      color: "from-emerald-500 to-teal-500",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl mb-6">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">You're All Set!</h3>
            <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mx-auto">
              Your personalized career transformation journey is ready to begin. Let's make your dreams a reality.
            </p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">Your Learning Dashboard</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <Calendar className="w-4 h-4 text-emerald-400 mr-3" />
                  <span className="text-sm">Week-by-week schedule</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <BarChart className="w-4 h-4 text-emerald-400 mr-3" />
                  <span className="text-sm">Progress tracking</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <Bookmark className="w-4 h-4 text-emerald-400 mr-3" />
                  <span className="text-sm">Resource library</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center text-slate-300">
                  <Award className="w-4 h-4 text-teal-400 mr-3" />
                  <span className="text-sm">Achievement badges</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <Download className="w-4 h-4 text-teal-400 mr-3" />
                  <span className="text-sm">PDF downloads</span>
                </div>
                <div className="flex items-center text-slate-300">
                  <Users className="w-4 h-4 text-teal-400 mr-3" />
                  <span className="text-sm">Community support</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-6">
            <div className="flex items-center mb-3">
              <Star className="w-5 h-5 text-purple-400 mr-2" />
              <h4 className="font-semibold text-purple-400">Success Guarantee</h4>
            </div>
            <p className="text-slate-300 text-sm">
              Join thousands of professionals who have successfully transformed their careers with Rastor AI. Your success is our mission.
            </p>
          </div>
        </div>
      )
    }
  ];

  const handleNext = async () => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCompletedSteps(prev => new Set([...prev, currentStep]));

    if (currentStep < steps.length - 1) {
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      setTimeout(() => {
        handleComplete();
        setIsAnimating(false);
      }, 300);
    }
  };

  const handlePrevious = async () => {
    if (isAnimating || currentStep === 0) return;

    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
      setIsAnimating(false);
    }, 300);
  };

  const handleComplete = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    localStorage.setItem('onboardingCompleted', 'true');
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem('onboardingCompleted', 'true');
    onClose();
  };

  const currentStepData = steps[currentStep];
  const IconComponent = currentStepData.icon;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`bg-slate-800/95 backdrop-blur-xl rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-slate-700/50 shadow-2xl transition-all duration-500 ${isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b border-slate-700/50">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 bg-gradient-to-br ${currentStepData.color} rounded-2xl flex items-center justify-center shadow-lg`}>
              <IconComponent className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{currentStepData.title}</h2>
              <p className="text-slate-400">{currentStepData.subtitle}</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-slate-400 hover:text-white transition-colors p-2 rounded-xl hover:bg-slate-700/50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <p className="text-slate-300 text-lg mb-8 leading-relaxed">
            {currentStepData.description}
          </p>
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-8 border-t border-slate-700/50">
          {/* Progress */}
          <div className="flex items-center space-x-3">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index < currentStep
                    ? 'bg-emerald-500 scale-110'
                    : index === currentStep
                      ? 'bg-emerald-400 scale-125'
                      : 'bg-slate-600'
                  }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0 || isAnimating}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 ${currentStep === 0 || isAnimating
                  ? 'text-slate-500 cursor-not-allowed'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Previous</span>
            </button>

            <button
              onClick={handleNext}
              disabled={isAnimating}
              className={`flex items-center space-x-2 bg-gradient-to-r ${currentStepData.color} text-white px-8 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${isAnimating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              <span>{currentStep === steps.length - 1 ? 'Get Started' : 'Next'}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial; 